import { z } from "zod/v4";

// Note that we can change this to whatever we want.
// But this is what it is for now.

export type Payload = {
	// This is the workpace information for that repo
	workspace: DocsApiWorkspace;
	repo: Repo;
};

export type DocsApiWorkspace = {
	display_name: string;
	id: string;
	sluggified_id: string;
	payment_status: string;
	avatar_url?: string;
};

export type Owner = {
	active?: boolean;
	login_name: string;
	avatar_url: string;
	created: string;
	email: string;
	full_name: string;
	id: number;
	last_login: string;
	username: string;
	source_id: number;
	workspace?: DocsApiWorkspace;
};

export type Repo = {
	allow_merge_commits: boolean;
	allow_rebase: boolean;
	allow_rebase_explicit: boolean;
	allow_rebase_update: boolean;
	allow_squash_merge: boolean;
	archived: boolean;
	archived_at: string;
	avatar_url: string;
	clone_url: string;
	created_at: string;
	default_allow_maintainer_edit: boolean;
	default_branch: string;
	default_delete_branch_after_merge: boolean;
	default_merge_style: string;
	description: string;
	empty: boolean;
	fork: boolean;
	forks_count: number;
	full_name: string;
	has_actions: boolean;
	has_issues: boolean;
	has_packages: boolean;
	has_projects: boolean;
	has_pull_requests: boolean;
	has_releases: boolean;
	has_wiki: boolean;
	html_url: string;
	id: number;
	ignore_whitespace_conflicts: boolean;
	internal: boolean;
	language: string;
	languages_url: string;
	link: string;
	mirror: boolean;
	mirror_interval: string;
	mirror_updated: string;
	name: string;
	open_issues_count: number;
	open_pr_counter: number;
	owner: Owner;
	original_url: string;
	private: boolean;
	release_counter: number;
	size: number;
	stars_count: number;
	template: boolean;
	updated_at: string;
	url: string;
	watchers_count: number;
	website: string;
	moment_title: string;
};

export const ALL_USERS = "AllUsers";
export const LOGGED_IN_USERS = "LoggedInUsers";

export interface Email {
	type: "Email";
	value: string;
}

export function isEmail(o: unknown): o is Email {
	return !!o && typeof o === "object" && "type" in o && o.type === "Email";
}

export type PermRelation = "reader" | "collaborator" | "owner";
export type NonOwnerPermRelation = Omit<PermRelation, "owner">;

export type DocPermission = "read" | "collaborate" | "share" | "own";
export type TablePermission = "read_all_rows" | "read_self_rows" | "write_rows";

export interface DocChangePermRequest {
	type: typeof ALL_USERS | typeof LOGGED_IN_USERS | "Username" | "Email" | "Workspace";
	// Optional for revoke — omitting relation means "revoke all access".
	relation?: PermRelation;
	value?: string;
}

export interface ShareType {
	// are we sharing publically or with a specific user?
	type: "grant" | "revoke";
	userType: "Username" | "Email";
	relation: NonOwnerPermRelation;
	user?: SearchUserResult;
	inviteEmail?: string;
}

export interface PermissionUpdate {
	type: "Username" | "Email" | "Workspace";
	action: "grant" | "revoke";
	relation?: NonOwnerPermRelation;
	user?: SearchUserResult;
	inviteEmail?: string;
}

export interface UpdateUserPermission {
	user: SearchUserResult;
	type: "grant" | "revoke";
	relation: PermRelation;
}

export interface UpdateWorkspacePermission {
	type: "grant" | "revoke";
	relation: PermRelation;
}

export interface InvitationUpdate {
	email: string;
	type: "grant" | "revoke";
	relation: PermRelation;
}

// These are users fetching from the sharing modal
// We do not send email for now as that is PII

/*
 [
  {
	"name": "Alex Clemmer",
	"nickname": "hausdorff",
	"picture": "https://avatars.githubusercontent.com/u/1409156?v=4",
	"user_id": "github|1409156"
  }
]
*/
export const SearchUserResultSchema = z.object({
	name: z.string(),
	nickname: z.string(),
	picture: z.string().url().optional().or(z.literal("")),
	user_id: z.string(),
});

export type SearchUserResult = z.infer<typeof SearchUserResultSchema>;

export const UserWithPermissionSchema = SearchUserResultSchema.extend({
	email: z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/),
	permission: z.enum(["read", "collaborate", "own"]),
});

export type UserWithPermission = z.infer<typeof UserWithPermissionSchema>;

export const InviteResultSchema = z.object({
	email: z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/),
	referrer: z.string(),
	relation: z.enum(["reader", "collaborator", "owner"]),
});

export type Invitation = z.infer<typeof InviteResultSchema>;

export const PermissionInfoSchema = z.object({
	permission: z.enum(["read", "write", "share", "own"]),
	users: z.array(SearchUserResultSchema),
	appliedToEveryoneInWorkspace: z.boolean(),
});

export type PermissionInfo = z.infer<typeof PermissionInfoSchema>;

export const AuthZedSubject = z.object({
	id: z.string(),
	type: z.string(),
	has_publish_password: z.boolean().optional(),
});

export type AuthZedSubjectSchema = z.Infer<typeof AuthZedSubject>;

export const PermissionsRolesResponseSchema = z.object({
	relation: z.object({
		// this order matters and it is prioritized by least to most privileged
		workspace: z.optional(z.array(AuthZedSubject)),
		reader: z.optional(z.array(AuthZedSubject)),
		collaborator: z.optional(z.array(AuthZedSubject)),
		owner: z.optional(z.array(AuthZedSubject)),
	}),
	users: z.record(z.string(), SearchUserResultSchema),
});

export type PermissionRolesResponse = z.infer<typeof PermissionsRolesResponseSchema>;

export type UserWithRelation = SearchUserResult & {
	relation: PermRelation;
};

export type SharedWithWorkspaceRelation = {
	relation: PermRelation;
	workspace_id: string; // Workspace IDs
};

export const MembershipResponseSchema = z.object({
	relation: z.object({
		owner: z.optional(z.array(z.string())),
		member: z.optional(z.array(z.string())),
	}),
	users: z.record(z.string(), SearchUserResultSchema),
	invites: z.optional(z.array(z.string())),
});

export type MembershipResponse = z.infer<typeof MembershipResponseSchema>;

export const UserWithMembershipSchema = SearchUserResultSchema.extend({
	relation: z.enum(["owner", "member"]),
});

export type UserWithMembership = z.infer<typeof UserWithMembershipSchema>;

const WorkspacesMembershipSchema = z.record(
	z.string().describe("The workspace name, as that is what will be displayed"),
	z.array(UserWithMembershipSchema).readonly()
);

export type WorkspacesMembership = z.infer<typeof WorkspacesMembershipSchema>;

export const DesktopAppDownloadsSchema = z.object({
	urls: z.record(
		z.enum([
			"linux_amd64_appimage",
			"linux_amd64_deb",
			"linux_x86_64_rpm",
			"macos_aarch64",
			"macos_x64",
			"windows_x64",
		]),
		z.object({ url: z.url() })
	),
});

export type DesktopAppDownloads = z.infer<typeof DesktopAppDownloadsSchema>;

// Schema for the new queryDocuments API response
export const QueryDocumentsWorkspaceSchema = z.object({
	id: z.string(),
	name: z.string(),
	host: z.string(),
	slug: z.string(),
	role: z.enum(["owner", "member", "guest"]),
	owner: z.boolean(),
	paymentStatus: z.string().nullish(),
	avatarUrl: z.string().nullish(),
});

export const QueryDocumentsEntrySchema = z.object({
	id: z.string(),
	title: z.string(),
	description: z.string().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
	type: z.string().optional(),
	workspace: QueryDocumentsWorkspaceSchema,
	permission: z.enum(["write", "read"]).optional(),
});

export type QueryDocumentsEntry = z.infer<typeof QueryDocumentsEntrySchema>;
export type QueryDocumentsResponse = QueryDocumentsEntry[];

export const V2RepoResponseSchema = z.object({
	ID: z.string(),
	DefaultBranch: z.string(),
});

export type V2RepoResponse = z.infer<typeof V2RepoResponseSchema>;

export const V2RepoGetResponseSchema = z.object({
	id: z.string(),
	workspaceID: z.string(),
	documentID: z.string(),
	defaultBranch: z.string(),
});

export type V2RepoGetResponse = z.infer<typeof V2RepoGetResponseSchema>;
